import React, { useState } from 'react';
import { Bookmark, BookmarkCheck, Volume2 } from 'lucide-react';
import { useSavedWords } from '../lib/useSavedWords';
import { speechService } from '../services/speechService';

const base =
  'inline-flex items-center justify-center gap-1.5 rounded-full border text-[0.9rem] font-medium transition-[background-color,border-color,color,transform] active:scale-95';

// Bookmark toggle backed by the browser's saved-words list
export const SaveButton = ({ word, compact = false, className = '' }) => {
  const { isSaved, toggle } = useSavedWords();
  const saved = isSaved(word);

  return (
    <button
      type="button"
      onClick={() => toggle(word)}
      aria-pressed={saved}
      aria-label={compact ? `${saved ? 'Remove' : 'Save'} ${word}` : undefined}
      title={saved ? 'Saved on this device' : 'Save to your words'}
      className={`${base} ${compact ? 'h-9 w-9' : 'h-10 px-4'} ${
        saved
          ? 'border-ink bg-ink text-paper'
          : 'border-ink/20 bg-surface/70 text-ink hover:border-ink'
      } ${className}`}
    >
      {saved ? <BookmarkCheck size={16} aria-hidden="true" /> : <Bookmark size={16} aria-hidden="true" />}
      {!compact && (saved ? 'Saved' : 'Save')}
    </button>
  );
};

// Speaks a word with the browser's text-to-speech
export const ListenButton = ({ word, className = '' }) => {
  const [playing, setPlaying] = useState(false);

  const play = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      await speechService.speak(word, { lang: 'en', rate: 0.85 });
    } catch (error) {
      console.error('Speech failed:', error);
    } finally {
      setPlaying(false);
    }
  };

  return (
    <button
      type="button"
      onClick={play}
      aria-label={`Hear how ${word} sounds`}
      className={`${base} h-9 w-9 ${
        playing ? 'border-ink bg-ink text-paper' : 'border-ink/20 bg-surface/70 text-ink hover:border-ink'
      } ${className}`}
    >
      {playing ? (
        <span className="sound-bars flex items-end gap-[2px]" aria-hidden="true">
          <span /><span /><span />
        </span>
      ) : (
        <Volume2 size={16} aria-hidden="true" />
      )}
    </button>
  );
};
